import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Check, Calendar, Clock, Users, QrCode, Home, Printer, UtensilsCrossed } from 'lucide-react';
import { Button } from '../components/ui/button';
import { api } from '../App';

const ConfirmationPage = () => {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const response = await api.get(`/bookings/${bookingId}`);
        setBooking(response.data);
      } catch (error) {
        console.error('Error fetching booking:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [bookingId]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Booking not found</p>
          <Link to="/">
            <Button>Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const isConfirmed = booking.status === 'confirmed' || booking.payment_status === 'deposit_paid' || booking.payment_status === 'fully_paid' || booking.payment_status === 'pay_on_arrival';

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-serif text-3xl font-bold text-foreground mb-2" data-testid="confirmation-title">
            {isConfirmed ? 'Booking Confirmed!' : 'Booking Received!'}
          </h1>
          <p className="text-muted-foreground">
            {isConfirmed 
              ? 'Your adventure awaits. Check your email for confirmation details.'
              : 'Your booking is pending confirmation. We\'ll notify you shortly.'}
          </p>
        </div>

        {/* Retain Copy Notice (RBC #9) */}
        <div className="flex items-center justify-between mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="retain-copy-notice">
          <p className="text-sm text-blue-800">Please retain a copy of this transaction for your records.</p>
          <Button variant="outline" size="sm" onClick={handlePrint} className="ml-3 flex-shrink-0" data-testid="print-btn">
            <Printer className="w-4 h-4 mr-1" />
            Print
          </Button>
        </div>

        {/* Booking Card */}
        <div className="bg-white rounded-2xl border border-border overflow-hidden shadow-lg mb-8" id="booking-receipt">
          {/* QR Code Section */}
          <div className="bg-primary/5 p-6 text-center border-b border-border">
            <p className="text-sm text-muted-foreground mb-3">Your Check-in QR Code</p>
            {booking.qr_code && (
              <div className="inline-block bg-white p-4 rounded-xl shadow-sm" data-testid="qr-code">
                <img 
                  src={`data:image/png;base64,${booking.qr_code}`}
                  alt="Check-in QR Code"
                  className="w-32 h-32"
                />
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-3">
              Show this code at check-in
            </p>
          </div>

          {/* Booking Details */}
          <div className="p-6 space-y-6">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Booking Reference</p>
              <p className="font-mono font-bold text-lg" data-testid="booking-id">{booking.id.slice(0, 8).toUpperCase()}</p>
            </div>

            <div>
              <p className="text-sm text-muted-foreground mb-1">Tour</p>
              <p className="font-serif font-bold text-xl">{booking.tour_title}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="font-semibold">{booking.date}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Time</p>
                  <p className="font-semibold">{booking.time_slot}</p>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Guests</p>
                <p className="font-semibold">
                  {booking.adults} Adult{booking.adults !== 1 ? 's' : ''}
                  {booking.children > 0 && `, ${booking.children} Child${booking.children !== 1 ? 'ren' : ''}`}
                </p>
              </div>
            </div>

            {booking.add_ons && booking.add_ons.length > 0 && (
              <div>
                <p className="text-sm text-muted-foreground mb-2">Add-ons</p>
                <ul className="space-y-1">
                  {booking.add_ons.map((addon, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      {addon.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Payment Status */}
            <div className="border-t border-border pt-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-muted-foreground">Total</span>
                <span className="font-bold text-lg" data-testid="booking-total">BSD ${booking.total_amount.toFixed(2)}</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Currency: Bahamian Dollar (BSD)</p>
              
              {booking.payment_status === 'fully_paid' && (
                <div className="flex items-center gap-2 text-green-600 text-sm" data-testid="payment-complete-badge">
                  <Check className="w-4 h-4" />
                  <span>Payment Complete</span>
                </div>
              )}

              {booking.payment_status === 'pay_on_arrival' && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm" data-testid="pay-on-arrival-badge">
                  <p className="font-semibold">Pay on Arrival</p>
                  <p className="text-xs mt-1">Please bring <strong>BSD ${booking.total_amount.toFixed(2)}</strong> in cash to pay at check-in.</p>
                </div>
              )}
              
              {booking.payment_status === 'pending' && (
                <Link to={`/checkout/${booking.id}`} className="block mt-3">
                  <Button className="w-full btn-primary" data-testid="complete-payment-btn">
                    Complete Payment
                  </Button>
                </Link>
              )}
            </div>

            {/* Merchant Info on Receipt */}
            <div className="border-t border-border pt-4 text-xs text-muted-foreground space-y-1" data-testid="receipt-merchant-info">
              <p className="font-semibold text-foreground text-sm">Harbor Point Holdings d/b/a The Village</p>
              <p>Athol Island, Nassau, The Bahamas</p>
              <p>+1 (242) 825-6265 | hello@thevillageatholisland.com</p>
              <p>Daily, 9:00 AM - 5:00 PM (EST)</p>
            </div>
          </div>
        </div>

        {/* Pre-Order Food CTA */}
        {isConfirmed && (
          <div className="mb-6 print:hidden" data-testid="preorder-cta">
            <Link to={`/pre-order/${booking.id}`}>
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-6 hover:shadow-md transition-all cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <UtensilsCrossed className="w-6 h-6 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif font-bold text-lg text-foreground">Pre-Order Your Meal</h3>
                    <p className="text-sm text-muted-foreground">Skip the wait! Order your food now and it will be ready when you arrive at The Village.</p>
                  </div>
                  <Button className="btn-primary flex-shrink-0" data-testid="preorder-btn">
                    Order Now
                  </Button>
                </div>
              </div>
            </Link>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 print:hidden">
          <Button variant="outline" className="flex-1" onClick={handlePrint} data-testid="print-receipt-btn">
            <Printer className="w-4 h-4 mr-2" />
            Print Receipt
          </Button>
          <Link to="/my-bookings" className="flex-1">
            <Button variant="outline" className="w-full" data-testid="view-bookings-btn">
              <QrCode className="w-4 h-4 mr-2" />
              View My Bookings
            </Button>
          </Link>
          <Link to="/" className="flex-1">
            <Button className="w-full btn-primary" data-testid="back-home-btn">
              <Home className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>

        {/* What to Bring */}
        <div className="mt-8 p-6 bg-white rounded-2xl border border-border">
          <h3 className="font-serif font-bold text-lg mb-4">What to Bring</h3>
          <ul className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-primary" />
              Swimsuit
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-primary" />
              Sunscreen
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-primary" />
              Towel
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-primary" />
              Camera
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-primary" />
              Cash for tips
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-primary" />
              Valid ID
            </li>
          </ul>
        </div>

        {/* Contact Info */}
        <div className="mt-6 text-center text-sm text-muted-foreground">
          <p>Questions? Contact us at <a href="mailto:hello@thevillageatholisland.com" className="text-primary hover:underline">hello@thevillageatholisland.com</a></p>
          <p>or call <a href="tel:+12428256265" className="text-primary hover:underline">+1 (242) 825-6265</a></p>
          <p className="mt-1">Daily, 9:00 AM - 5:00 PM (EST)</p>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationPage;
